/**
 * RPC Request Batcher with throttling
 *
 * Collects RPC requests over a short time window and sends them efficiently.
 *
 * Due to server authentication design (signature covers method+params),
 * authenticated requests are sent individually while unauthenticated
 * requests can be truly batched together.
 */
/**
 * RpcBatcher - Batches RPC requests with throttling
 *
 * @example
 * ```typescript
 * const batcher = new RpcBatcher('https://api.example.com', {
 *   delay: 10,
 *   maxBatchSize: 50
 * })
 *
 * // Requests made within the delay window are batched
 * const [result1, result2] = await Promise.all([
 *   batcher.add({ method: 'getOffer', params: {...} }, null),
 *   batcher.add({ method: 'getOffer', params: {...} }, null)
 * ])
 * ```
 */
export class RpcBatcher {
    constructor(baseUrl, options = {}) {
        this.baseUrl = baseUrl;
        this.queue = [];
        this.flushTimer = null;
        this.delay = options.delay ?? 10;
        this.maxBatchSize = options.maxBatchSize ?? 50;
    }
    /**
     * Add a request to the batch queue
     * @param request - The RPC request
     * @param authHeaders - Auth headers for authenticated requests, null for unauthenticated
     * @returns Promise that resolves with the request result
     */
    add(request, authHeaders) {
        return new Promise((resolve, reject) => {
            this.queue.push({ request, authHeaders, resolve, reject });
            this.scheduleFlush();
        });
    }
    /**
     * Schedule a flush after the delay
     */
    scheduleFlush() {
        if (this.flushTimer)
            return;
        this.flushTimer = setTimeout(() => {
            this.flushTimer = null;
            this.flush();
        }, this.delay);
    }
    /**
     * Flush all queued requests
     */
    async flush() {
        if (this.queue.length === 0)
            return;
        const items = this.queue;
        this.queue = [];
        // Separate authenticated vs unauthenticated requests
        const unauthenticated = [];
        const authenticated = [];
        for (const item of items) {
            if (item.authHeaders) {
                authenticated.push(item);
            }
            else {
                unauthenticated.push(item);
            }
        }
        // Process unauthenticated requests in batches
        await this.processUnauthenticatedBatches(unauthenticated);
        // Process authenticated requests individually (each needs unique signature)
        await this.processAuthenticatedRequests(authenticated);
    }
    /**
     * Process unauthenticated requests in batches
     */
    async processUnauthenticatedBatches(items) {
        if (items.length === 0)
            return;
        // Split into chunks of maxBatchSize
        for (let i = 0; i < items.length; i += this.maxBatchSize) {
            const chunk = items.slice(i, i + this.maxBatchSize);
            await this.sendBatch(chunk, null);
        }
    }
    /**
     * Process authenticated requests individually
     * Each authenticated request needs its own HTTP call because
     * the signature covers the specific method+params
     */
    async processAuthenticatedRequests(items) {
        // Send all authenticated requests in parallel, each as its own batch of 1
        await Promise.all(items.map(item => this.sendBatch([item], item.authHeaders)));
    }
    /**
     * Send a batch of requests
     */
    async sendBatch(items, authHeaders) {
        try {
            const requests = items.map(item => item.request);
            const headers = {
                'Content-Type': 'application/json',
            };
            if (authHeaders) {
                Object.assign(headers, authHeaders);
            }
            const response = await fetch(`${this.baseUrl}/rpc`, {
                method: 'POST',
                headers,
                body: JSON.stringify(requests), // Always send as array
            });
            if (!response.ok) {
                const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
                items.forEach(item => item.reject(error));
                return;
            }
            const results = await response.json();
            // Match responses to requests (server returns array in same order)
            items.forEach((item, index) => {
                const result = results[index];
                if (!result) {
                    item.reject(new Error('Missing response from server'));
                }
                else if (!result.success) {
                    item.reject(new Error(result.error || 'RPC call failed'));
                }
                else {
                    item.resolve(result.result);
                }
            });
        }
        catch (error) {
            // Network or parsing error - reject all
            items.forEach(item => item.reject(error));
        }
    }
    /**
     * Flush immediately (useful for cleanup/testing)
     */
    async flushNow() {
        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
            this.flushTimer = null;
        }
        await this.flush();
    }
}
